import React, { useEffect } from 'react';

interface UXCROOptimizerProps {
  language?: 'it' | 'es' | 'en';
  enableHeatmapTracking?: boolean;
  enableScrollTracking?: boolean;
  enableFormAnalytics?: boolean;
  enableExitIntent?: boolean;
  enableMobileOptimization?: boolean;
  enableAccessibility?: boolean;
  enableMicroInteractions?: boolean;
  enableABTesting?: boolean;
}

const UXCROOptimizer: React.FC<UXCROOptimizerProps> = ({
  language = 'it',
  enableHeatmapTracking = true,
  enableScrollTracking = true,
  enableFormAnalytics = true,
  enableExitIntent = true,
  enableMobileOptimization = true,
  enableAccessibility = true,
  enableMicroInteractions = true,
  enableABTesting = false
}) => {
  
  useEffect(() => {
    // UX/CRO Optimization Implementation
    const implementUXOptimizations = () => {
      
      // Scroll tracking for engagement analytics
      if (enableScrollTracking) {
        let scrollDepth = 0;
        const trackScrollDepth = () => {
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const currentDepth = Math.round(((scrollTop + windowHeight) / documentHeight) * 100);
          
          if (currentDepth > scrollDepth && currentDepth % 25 === 0) {
            scrollDepth = currentDepth;
            // Track scroll depth milestone
            if (window.gtag) {
              window.gtag('event', 'scroll_depth', {
                event_category: 'engagement',
                event_label: `${scrollDepth}%`,
                value: scrollDepth
              });
            }
          }
        };
        
        window.addEventListener('scroll', trackScrollDepth, { passive: true });
      }
      
      // Form analytics and optimization
      if (enableFormAnalytics) {
        const forms = document.querySelectorAll('form');
        forms.forEach(form => {
          // Track form start
          form.addEventListener('focusin', () => {
            if (window.gtag) {
              window.gtag('event', 'form_start', {
                event_category: 'form_interaction',
                event_label: form.getAttribute('name') || 'contact_form'
              });
            }
          }, { once: true });
          
          // Track form abandonment
          const inputs = form.querySelectorAll('input, textarea, select');
          inputs.forEach(input => {
            input.addEventListener('blur', () => {
              if (window.gtag) {
                window.gtag('event', 'form_field_complete', {
                  event_category: 'form_interaction',
                  event_label: input.getAttribute('name') || 'field'
                });
              }
            });
          });
        });
      }
      
      // Exit intent detection
      if (enableExitIntent) {
        let exitIntentTriggered = false;
        const handleExitIntent = (e: MouseEvent) => {
          if (!exitIntentTriggered && e.clientY <= 0) {
            exitIntentTriggered = true;
            if (window.gtag) {
              window.gtag('event', 'exit_intent', {
                event_category: 'user_behavior',
                event_label: 'mouse_leave'
              });
            }
            // Could trigger exit intent popup here
          }
        };
        
        document.addEventListener('mouseleave', handleExitIntent);
      }
      
      // Mobile optimization enhancements
      if (enableMobileOptimization) {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile) {
          // Add mobile-specific optimizations
          document.body.classList.add('mobile-optimized');
          
          // Optimize touch targets
          const buttons = document.querySelectorAll('button, a, input[type="submit"]');
          buttons.forEach(button => {
            const element = button as HTMLElement;
            const rect = element.getBoundingClientRect();
            if (rect.height < 44) {
              element.style.minHeight = '44px';
              element.style.padding = '12px';
            }
          });
        }
      }
      
      // Accessibility enhancements
      if (enableAccessibility) {
        // Add skip navigation link
        const skipLink = document.createElement('a');
        skipLink.href = '#main-content';
        skipLink.textContent = language === 'it' ? 'Salta al contenuto principale' : 
                              language === 'es' ? 'Saltar al contenido principal' : 
                              'Skip to main content';
        skipLink.className = 'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded z-50';
        document.body.insertBefore(skipLink, document.body.firstChild);
        
        // Enhance focus management
        const focusableElements = document.querySelectorAll(
          'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
        );
        
        focusableElements.forEach(element => {
          element.addEventListener('focus', () => {
            element.classList.add('focus-visible');
          });
          
          element.addEventListener('blur', () => {
            element.classList.remove('focus-visible');
          });
        });
      }
      
      // Micro-interactions for better UX
      if (enableMicroInteractions) {
        // Add hover effects to interactive elements
        const interactiveElements = document.querySelectorAll('button, a, .card, .service-item');
        interactiveElements.forEach(element => {
          element.addEventListener('mouseenter', () => {
            element.classList.add('hover-effect');
          });
          
          element.addEventListener('mouseleave', () => {
            element.classList.remove('hover-effect');
          });
        });
        
        // Add click feedback
        document.addEventListener('click', (e) => {
          const target = e.target as HTMLElement;
          if (target.tagName === 'BUTTON' || target.closest('button')) {
            target.classList.add('clicked');
            setTimeout(() => target.classList.remove('clicked'), 150);
          }
        });
      }
      
      // A/B Testing setup (if enabled)
      if (enableABTesting) {
        const variant = Math.random() < 0.5 ? 'A' : 'B';
        document.body.setAttribute('data-ab-variant', variant);
        
        if (window.gtag) {
          window.gtag('event', 'ab_test_variant', {
            event_category: 'ab_testing',
            event_label: variant,
            custom_parameter_variant: variant
          });
        }
      }
    };
    
    // Run optimizations after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', implementUXOptimizations);
    } else {
      implementUXOptimizations();
    }
    
    // Performance monitoring
    const monitorPerformance = () => {
      // Monitor page load time
      window.addEventListener('load', () => {
        const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
        if (window.gtag) {
          window.gtag('event', 'page_load_time', {
            event_category: 'performance',
            value: Math.round(loadTime),
            custom_parameter_load_time: loadTime
          });
        }
      });
      
      // Monitor user engagement time
      let startTime = Date.now();
      let isActive = true;
      
      const trackEngagement = () => {
        if (isActive) {
          const engagementTime = Date.now() - startTime;
          if (window.gtag) {
            window.gtag('event', 'user_engagement', {
              event_category: 'engagement',
              value: Math.round(engagementTime / 1000),
              custom_parameter_engagement_time: engagementTime
            });
          }
        }
      };
      
      // Track when user becomes inactive
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          isActive = false;
          trackEngagement();
        } else {
          isActive = true;
          startTime = Date.now();
        }
      });
      
      // Track engagement on page unload
      window.addEventListener('beforeunload', trackEngagement);
    };
    
    monitorPerformance();
    
  }, [language, enableHeatmapTracking, enableScrollTracking, enableFormAnalytics, 
      enableExitIntent, enableMobileOptimization, enableAccessibility, 
      enableMicroInteractions, enableABTesting]);

  return (
    <>
      {/* Critical UX CSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* Focus management */
          .focus-visible {
            outline: 2px solid #3b82f6;
            outline-offset: 2px;
          }
          
          /* Skip link */
          .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
          }
          
          /* Hover effects */
          .hover-effect {
            transform: translateY(-2px);
            transition: transform 0.2s ease;
          }
          
          /* Click feedback */
          .clicked {
            transform: scale(0.98);
            transition: transform 0.1s ease;
          }
          
          /* Mobile optimizations */
          .mobile-optimized button,
          .mobile-optimized a {
            min-height: 44px;
            min-width: 44px;
          }
          
          /* Smooth scrolling */
          html {
            scroll-behavior: smooth;
          }
          
          /* Reduced motion for accessibility */
          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
            
            html {
              scroll-behavior: auto;
            }
          }
          
          /* High contrast mode support */
          @media (prefers-contrast: high) {
            button, a {
              border: 2px solid;
            }
          }
        `
      }} />
    </>
  );
};

export { UXCROOptimizer };
export default UXCROOptimizer;